import React, {useMemo} from "react";
import { blue, lightBlue } from '@mui/material/colors'

import {
    getSeason,
    getSeasonLabel,
    extractAvailableSeasons,
    generateSumByKey,
} from "../utils";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";


const HNGraph = ({data: originalData, station}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const data = useMemo(() => (originalData.map(({timestamp, ...rest}) => {
            return {
                timestamp,
                season: getSeason(timestamp),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const seasonLabels = availableSeasons.map(season => getSeasonLabel(season));

    const seasonStat = generateSumByKey(data, ['hn'], 'season');
    const hnValues = availableSeasons.map(season => seasonStat[season]?.hn?.sum ?? null);
    const hnMean = hnValues.length > 0 ? Math.round(hnValues.reduce((acc, value) => acc + value, 0) / hnValues.length) : null;

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `${station?.code ?? 'station'}_hn`
        },

        title: {
            text: `${station?.code ?? ''} - ${station?.name ?? ''} (${station?.elevation ?? '----'} m)`
        },
        subtitle: {
            text: t('hnGraph.title')
        },
        series: [
            {
                data: availableSeasons.map(season => seasonStat[season]?.hn?.sum ?? null),
                type: 'column',
                name: t('hnGraph.legend.hnCum'),
                color: lightBlue[500],
                yAxis: 0,
            },
            {
                data: availableSeasons.map(_ => hnMean ?? null),
                type: 'line',
                name: t('hnGraph.legend.hnCumMean'),
                color: blue[900],
                yAxis: 0,
            }
        ],
        xAxis: {
            categories: seasonLabels
        },
        yAxis: [
            {
                title: {
                    text: 'HN [cm]'
                }
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            },
            column: {
                dataLabels: {
                    enabled: true,
                    crop: false,
                    overflow: 'none',
                    formatter: function() {
                        return this.y;
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: 500,
            ...chartLogoOptions,
        },
        ...creditsOptions,
    }

    return <HighchartsReact
        highcharts={Highcharts}
        options={options}
    />

}

export default HNGraph;